import { forwardRef } from 'react'
import { clsx } from 'clsx'
import { motion } from 'motion/react'
import type { ComponentProps, PropsWithChildren, ReactElement } from 'react'
import * as AccordionPrimitives from '@radix-ui/react-accordion'
import { MinusIcon } from '../../icons/MinusIcon'
import { PlusIcon } from '../../icons/PlusIcon'
import { closeIcon, content, item, openIcon, reducedContent, root, trigger, TRIGGER_ICON_SIZE } from './Accordion.css'

const Root = ({ className, ...forwardedProps }: ComponentProps<typeof AccordionPrimitives.Root>) => (
  <AccordionPrimitives.Root className={clsx(root, className)} {...forwardedProps} />
)

const Trigger = ({ className, ...forwardedProps }: ComponentProps<typeof AccordionPrimitives.Trigger>) => (
  <AccordionPrimitives.Trigger className={clsx(trigger, className)} {...forwardedProps} />
)

const Item = ({ className, ...forwardedProps }: ComponentProps<typeof AccordionPrimitives.Item>) => (
  <AccordionPrimitives.Item className={clsx(item, className)} {...forwardedProps} />
)

type HeaderWithTriggerProps = PropsWithChildren<unknown> & {
  icon?: ReactElement
  className?: string
}

const HeaderWithTrigger = ({ children, className }: HeaderWithTriggerProps) => {
  return (
    <AccordionPrimitives.Header>
      <Trigger className={className}>
        {children}

        <PlusIcon size={TRIGGER_ICON_SIZE} className={openIcon} />
        <MinusIcon size={TRIGGER_ICON_SIZE} className={closeIcon} />
      </Trigger>
    </AccordionPrimitives.Header>
  )
}

type ContentProps = AccordionPrimitives.AccordionContentProps & { open: boolean; fullWidth?: boolean }

const Content = forwardRef<HTMLDivElement, ContentProps>(
  ({ children, open, fullWidth = false, className, ...props }, forwardedRef) => {
    return (
      <AccordionPrimitives.Content
        className={clsx(content, !fullWidth && reducedContent, className)}
        {...props}
        ref={forwardedRef}
        forceMount={true}
      >
        <motion.div
          initial={false}
          transition={{
            ease: [0.65, 0.05, 0.36, 1],
            duration: 0.4,
          }}
          variants={{
            opened: {
              height: 'auto',
            },
            closed: {
              height: 0,
            },
          }}
          animate={open ? 'opened' : 'closed'}
        >
          {children}
        </motion.div>
      </AccordionPrimitives.Content>
    )
  },
)
Content.displayName = 'AccordionContent'

export const Accordion = {
  Root,
  Item,
  HeaderWithTrigger,
  Trigger,
  Content,
}
